import { Resp } from '@paradigm/api';
import { AsyncResp } from '@paradigm/api/src/fetch-api';
import { retryExecutor } from '@paradigm/utils/src/retry';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import { DrfqService } from '#/unified-rfqs/services/drfq';
import { Order } from '#/unified-rfqs/entities/drfq/orders';
import { CreateOrderReq } from '#/unified-rfqs/repositories/drfq-types';
import { Report } from '#/unified-rfqs/ui/one-click-trade/types';

/**
 * We're retrying, but it should fail fast to give fast feedback to the
 * user. This will result in 3 attempts (first + 2 retries) with waits
 * of 50ms and 75ms before the first and second retries respectively.
 */
const ORDER_SUBMIT_RETRIES = { retries: 2, factor: 1.5, minTimeout: 50 };

const INVALID_PRICE = 3304;

export function useSubmitOrderWithRetries(): (
  request: CreateOrderReq,
) => AsyncResp<Report> {
  const drfqService = useDrfqService();

  return async (request: CreateOrderReq): AsyncResp<Report> => {
    const response = await submitOrderWithRetries(request, drfqService);
    const { side, signal } = request;
    const { ok, message } = response;
    return {
      ok: true,
      status: 0,
      error: '',
      message: '',
      data: { side, signal, message, ok },
    };
  };
}

async function submitOrderWithRetries(
  request: CreateOrderReq,
  drfqService: DrfqService,
): Promise<Resp<Order>> {
  const { value } = await retryExecutor(async () => {
    const resp = await drfqService.createOrder(request);

    if (!resp.ok) {
      const isAborted = request.signal?.aborted === true;
      const isRetriable =
        !isAborted &&
        (resp.status === 0 /* Unknown network error */ ||
          resp.status >= 502 /* Infra error */ ||
          /* Retry on invalid price to prevent a transitive failure when
           * the user is trying to update their quotes to new values that
           * cross one of its current quotes */
          resp.data.code === INVALID_PRICE);

      return { ok: false, retriable: isRetriable, value: resp };
    }

    return { ok: true, value: resp };
  }, ORDER_SUBMIT_RETRIES);

  return value;
}
